import { ArrowLeft, ArrowRight } from 'lucide-react';
import { getMode, setMode, subscribe } from '@/lib/calendarScopeStore';
import { useSyncExternalStore } from 'react';

/**
 * CalendarScopeToggle — compact two-way switch mirroring the
 * BenchmarkComparison "Dividend ↔ Index" pattern. Flips between
 * "Portfolio only" and "Portfolio + Watchlist" for the dividend
 * calendar / income heatmap consumers.
 *
 * State lives in `calendarScopeStore` so the toggle on the
 * Dashboard 12-month heatmap card and the toggle on the standalone
 * Dividend Calendar page stay in sync (flipping one flips the
 * other). Persists to localStorage.
 *
 * Round 92o++++++++N++++++++++ (Jun 2026).
 */
export default function CalendarScopeToggle({ size = 'sm', testIdPrefix = 'calendar-scope' }) {
  const mode = useSyncExternalStore(subscribe, getMode, getMode);
  const isPortfolio = mode === 'portfolio';
  const flip = () => setMode(isPortfolio ? 'both' : 'portfolio');

  // Two compact size variants — `sm` for inline use inside the
  // heatmap header bar, `md` for the standalone calendar page top
  // strip (slightly larger so the legend label is readable above the
  // big calendar grid).
  const dims =
    size === 'md'
      ? {
          track: 'w-12 h-6',
          thumb: 'w-5 h-5',
          off: 'translate-x-0.5',
          on: 'translate-x-[26px]',
          label: 'text-[11px]',
          hint: 'text-[10px]',
        }
      : {
          track: 'w-10 h-5',
          thumb: 'w-4 h-4',
          off: 'translate-x-0.5',
          on: 'translate-x-[22px]',
          label: 'text-[10px]',
          hint: 'text-[9px]',
        };

  return (
    <div
      className="inline-flex items-center gap-1.5 rounded-md border border-violet-200 dark:border-violet-800/60 bg-violet-50/50 dark:bg-violet-950/20 px-2 py-1"
      data-testid={`${testIdPrefix}-toggle-wrap`}
    >
      <span className={`font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300 whitespace-nowrap ${dims.label}`}>
        {isPortfolio ? 'Portfolio' : 'Port + Watch'}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={!isPortfolio}
        aria-label={`Switch to ${isPortfolio ? 'Portfolio + Watchlist' : 'Portfolio only'}`}
        onClick={flip}
        data-testid={`${testIdPrefix}-toggle`}
        className={`relative ${dims.track} rounded-full bg-violet-500 dark:bg-violet-600 transition-colors shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-violet-400`}
      >
        <span
          className={`absolute top-0.5 left-0 ${dims.thumb} rounded-full bg-white shadow-md transition-transform ${
            isPortfolio ? dims.off : dims.on
          }`}
        />
      </button>
      <button
        type="button"
        onClick={flip}
        data-testid={`${testIdPrefix}-switch-hint`}
        className={`inline-flex items-center gap-0.5 font-semibold text-muted-foreground hover:text-violet-700 dark:hover:text-violet-300 transition-colors whitespace-nowrap ${dims.hint}`}
        title={isPortfolio ? 'Include watchlist tickers in calendar/heatmap' : 'Show only your owned positions'}
      >
        {isPortfolio ? (
          <>
            <span>+ Watch</span>
            <ArrowRight className="w-3 h-3" />
          </>
        ) : (
          <>
            <ArrowLeft className="w-3 h-3" />
            <span>Portfolio</span>
          </>
        )}
      </button>
    </div>
  );
}
